/*
  DIREVULF — predictive search.

  WHAT THIS ATTACHES TO
    The store's real search fields: the header's, the mobile header's, and the
    one on the search results page. Each keeps working exactly as it did with
    JavaScript switched off — it is an ordinary GET form to /search — and this
    file only adds a suggestion list underneath it. Nothing is replaced.

  WHERE THE RESULTS COME FROM
    Shopify's own predictive search endpoint, rendered by
    sections/predictive-search.liquid through the Section Rendering API. The
    markup that arrives is Liquid-rendered, so prices go through the money
    filter and are always ₹, and nothing is formatted, scored, re-ordered or
    invented here. The order the suggestions appear in is Shopify's own
    relevance order.

    The one thing this file decides is WHICH product properties Shopify is
    asked to match against — see FIELDS below.

  KEYBOARD
    The field is a combobox over a listbox, following the W3C ARIA authoring
    practices pattern that Shopify's own predictive search example uses. Focus
    never leaves the field; the highlighted suggestion is pointed at with
    aria-activedescendant. Arrow keys move, Enter opens the highlighted
    suggestion, Enter with nothing highlighted submits the search as usual,
    and Escape drops the highlight and then closes the list.

  VOICE
    The live microphone in the header is header.js's, and it is not touched
    here. The [data-dv-voice] button below belongs to snippets/search-panel.liquid;
    it is bound only if that panel is actually on the page.
*/
(function () {
  'use strict';

  var RECENT_KEY = 'dv:recent-searches';
  var RECENT_MAX = 6;
  var DEBOUNCE = 220;
  var MIN_CHARS = 2;

  /* Which product properties Shopify matches the query against. Shopify's own
     default is title, product_type, variant_title and vendor — which meant a
     shopper typing a fandom ("Anime", "Games", "TV Series") matched nothing,
     because in this catalogue the fandom is carried in the product's tags.
     `tag` is added for exactly that reason. `body` is deliberately left out:
     matching inside descriptions makes almost every query return almost every
     product.

     These are Shopify's documented searchable properties, not a field list of
     our own invention, and the matching itself remains entirely Shopify's. */
  var FIELDS = 'title,product_type,variants.title,vendor,tag';

  var FORM_SELECTOR = 'form.header-search, form.mobile-search, form.dv-search__form, [data-dv-search-form]';
  var OPTION_SELECTOR = '.dv-psearch__item, .dv-psearch__chips a, .dv-psearch__all';

  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* Every user-facing string stays in the locale files. snippets/search-suggest.liquid
     puts them on the page; the fallbacks below only ever appear if that snippet
     is missing, and never a "Translation missing" line. */
  var strings = (function () {
    var out = {};
    ['dv-suggest-strings', 'dv-search-strings'].forEach(function (id) {
      var node = document.getElementById(id);
      if (!node) return;
      try {
        var parsed = JSON.parse(node.textContent);
        Object.keys(parsed).forEach(function (key) { if (out[key] == null) out[key] = parsed[key]; });
      } catch (e) { /* keep the fallbacks */ }
    });
    return out;
  })();

  function text(key, fallback) {
    var value = strings[key];
    if (typeof value !== 'string') return fallback;
    value = value.trim();
    if (!value || value.indexOf('Translation missing') === 0) return fallback;
    return value;
  }

  /* --------------------------------------------------------------- recent */
  /* One shared list, so a term typed in the header is offered again on the
     results page. It never leaves this browser. */
  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (t) { return typeof t === 'string'; }) : [];
    } catch (e) { return []; }
  }

  function writeRecent(list) {
    try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch (e) { /* ignore */ }
  }

  function rememberTerm(term) {
    term = String(term || '').trim();
    if (term.length < MIN_CHARS) return;
    var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
    list.unshift(term);
    writeRecent(list);
  }

  function endpoint(term) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base.replace(/\/$/, '') +
      '/search/suggest?q=' + encodeURIComponent(term) +
      '&resources[type]=product,collection,page,query' +
      '&resources[limit]=6' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=' + encodeURIComponent(FIELDS) +
      '&section_id=predictive-search';
  }

  var seq = 0;
  var openOne = null;

  function createSuggest(form) {
    var input = form.querySelector('input[type="search"], input[name="q"]');
    if (!input || input.hasAttribute('data-dv-suggest-bound')) return null;
    input.setAttribute('data-dv-suggest-bound', '');

    var id = 'DvSuggest' + (++seq);

    /* ------------------------------------------------------------ markup */
    /* Built with DOM calls rather than a string of HTML, and every piece of
       text set with textContent, so nothing a shopper types can ever become
       markup. The list itself is appended to <body> and positioned against
       the field: the header is sticky and clips its own overflow, and a list
       inside it would be cut off. */
    var host = document.createElement('div');
    host.className = 'dv-suggest';
    host.setAttribute('data-dv-suggest', '');
    host.hidden = true;

    var status = document.createElement('p');
    status.className = 'dv-suggest__status visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    var recentWrap = document.createElement('div');
    recentWrap.className = 'dv-suggest__recent';
    recentWrap.hidden = true;
    var recentHead = document.createElement('div');
    recentHead.className = 'dv-search__recent-head';
    var recentTitle = document.createElement('h3');
    recentTitle.className = 'dv-psearch__title';
    recentTitle.textContent = text('recent', 'Recent searches');
    var recentClear = document.createElement('button');
    recentClear.type = 'button';
    recentClear.className = 'dv-link dv-suggest__forget';
    recentClear.textContent = text('recentClear', 'Clear');
    recentHead.appendChild(recentTitle);
    recentHead.appendChild(recentClear);
    var recentList = document.createElement('ul');
    recentList.className = 'dv-psearch__chips';
    recentWrap.appendChild(recentHead);
    recentWrap.appendChild(recentList);

    var loading = document.createElement('p');
    loading.className = 'dv-suggest__note';
    loading.hidden = true;
    loading.textContent = text('loading', 'Searching…');

    var errorNote = document.createElement('p');
    errorNote.className = 'dv-suggest__note dv-suggest__note--error';
    errorNote.hidden = true;
    errorNote.textContent = text('errorText', 'Search is unavailable right now. Press Enter to search the store.');

    var results = document.createElement('div');
    results.className = 'dv-suggest__results';
    results.id = id + 'List';
    results.hidden = true;
    results.setAttribute('role', 'listbox');
    results.setAttribute('aria-label', text('label', 'Search suggestions'));

    host.appendChild(status);
    host.appendChild(recentWrap);
    host.appendChild(loading);
    host.appendChild(errorNote);
    host.appendChild(results);
    document.body.appendChild(host);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', results.id);
    input.setAttribute('autocomplete', 'off');

    /* ------------------------------------------------------------- state */
    var timer = null;
    var controller = null;
    var options = [];
    var activeIndex = -1;
    var lastTerm = '';

    function say(message) { status.textContent = message || ''; }

    function place() {
      var box = form.getBoundingClientRect();
      var viewport = document.documentElement.clientWidth;
      var width = Math.max(box.width, Math.min(320, viewport - 16));
      var left = box.left;
      if (left + width > viewport - 8) left = viewport - width - 8;
      if (left < 8) left = 8;
      host.style.left = Math.round(left) + 'px';
      host.style.top = Math.round(box.bottom + 6) + 'px';
      host.style.width = Math.round(width) + 'px';
      host.style.maxHeight = Math.max(180, Math.round(window.innerHeight - box.bottom - 24)) + 'px';
    }

    /* The header is sticky and compacts as the page scrolls; when the field it
       belongs to is taken off screen the list goes with it rather than being
       left floating at the corner of the window. */
    function reposition() {
      if (host.hidden) return;
      var box = form.getBoundingClientRect();
      if (!box.width && !box.height) { close(); return; }
      place();
    }

    function open() {
      if (openOne && openOne !== api) openOne.close();
      openOne = api;
      if (host.hidden) {
        host.hidden = false;
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);
      }
      input.setAttribute('aria-expanded', 'true');
      place();
    }

    function close() {
      if (host.hidden) return;
      host.hidden = true;
      setActive(-1);
      input.setAttribute('aria-expanded', 'false');
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      if (openOne === api) openOne = null;
    }

    function setState(state) {
      recentWrap.hidden = state !== 'recent';
      loading.hidden = state !== 'loading';
      errorNote.hidden = state !== 'error';
      results.hidden = state !== 'results';
      if (state !== 'results') {
        options = [];
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
      }
      if (state === 'closed') { close(); return; }
      open();
    }

    /* ------------------------------------------------------------- recent */
    function renderRecent() {
      var list = readRecent();
      recentList.textContent = '';
      if (!list.length) return false;
      list.forEach(function (term) {
        var li = document.createElement('li');
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'dv-chip';
        button.textContent = term;
        button.setAttribute('data-dv-recent-term', term);
        li.appendChild(button);
        recentList.appendChild(li);
      });
      return true;
    }

    recentClear.addEventListener('click', function () {
      writeRecent([]);
      renderRecent();
      setState('closed');
      input.focus();
    });

    recentList.addEventListener('click', function (event) {
      var chip = event.target.closest ? event.target.closest('[data-dv-recent-term]') : null;
      if (!chip) return;
      input.value = chip.getAttribute('data-dv-recent-term');
      input.focus();
      run(input.value);
    });

    /* -------------------------------------------------------- suggestions */
    function indexOptions() {
      options = $$(OPTION_SELECTOR, results);
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');

      /* The group lists are structure, not options — leaving them as lists
         inside a listbox makes a screen reader count every suggestion twice. */
      $$('ul, li', results).forEach(function (node) { node.setAttribute('role', 'presentation'); });

      options.forEach(function (node, i) {
        node.id = id + 'Option' + i;
        node.setAttribute('role', 'option');
        node.setAttribute('aria-selected', 'false');
        node.setAttribute('tabindex', '-1');
      });

      input.setAttribute('aria-expanded', options.length ? 'true' : 'false');
    }

    function setActive(index) {
      if (activeIndex > -1 && options[activeIndex]) {
        options[activeIndex].classList.remove('is-active');
        options[activeIndex].setAttribute('aria-selected', 'false');
      }
      if (index < 0 || index >= options.length) {
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        return;
      }
      activeIndex = index;
      var node = options[activeIndex];
      node.classList.add('is-active');
      node.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', node.id);
      try { node.scrollIntoView({ block: 'nearest' }); } catch (e) { /* older browser */ }
    }

    function move(step) {
      if (!options.length) return;
      var next = activeIndex + step;
      if (next < 0) next = options.length - 1;
      if (next >= options.length) next = 0;
      setActive(next);
    }

    /* The query, marked inside the suggestion text. Assembled from text nodes
       and a <mark>, never from a string of HTML.

       The three pieces go inside ONE wrapper span rather than straight into
       the suggestion. A chip is an inline-flex box, and a flex container
       trims the whitespace at the edges of each of its anonymous text items —
       splitting "Men's Hoodies" in place therefore rendered it as
       "Men'sHoodies". One wrapper means one flex item and ordinary text
       flow inside it. */
    function highlight(term) {
      term = String(term || '');
      if (!term) return;
      var needle = term.toLowerCase();
      $$('.dv-psearch__name, .dv-chip', results).forEach(function (node) {
        var body = node.textContent || '';
        var at = body.toLowerCase().indexOf(needle);
        if (at < 0) return;
        var mark = document.createElement('mark');
        mark.className = 'dv-psearch__hit';
        mark.textContent = body.slice(at, at + term.length);
        var wrap = document.createElement('span');
        wrap.className = 'dv-psearch__text';
        wrap.appendChild(document.createTextNode(body.slice(0, at)));
        wrap.appendChild(mark);
        wrap.appendChild(document.createTextNode(body.slice(at + term.length)));
        node.textContent = '';
        node.appendChild(wrap);
      });
    }

    function showRecent() {
      if (!renderRecent()) { setState('closed'); return; }
      setState('recent');
    }

    function run(term) {
      term = String(term || '').trim();
      lastTerm = term;

      if (term.length < MIN_CHARS) {
        if (controller) { controller.abort(); controller = null; }
        results.textContent = '';
        showRecent();
        say('');
        return;
      }

      setState('loading');
      say(text('loading', 'Searching…'));

      if (controller) controller.abort();
      controller = ('AbortController' in window) ? new AbortController() : null;

      fetch(endpoint(term), controller ? { signal: controller.signal } : undefined)
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (term !== lastTerm) return;
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-dv-predictive-root]');
          if (!fresh) throw new Error('unexpected response');
          results.textContent = '';
          results.appendChild(fresh);
          setState('results');
          indexOptions();
          highlight(term);
          say('');
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          /* The form still works: Enter searches the store. The note says so
             rather than pretending there were no matches. */
          setState('error');
          say(text('errorText', ''));
        });
    }

    /* ------------------------------------------------------------ events */
    input.addEventListener('input', function () {
      window.clearTimeout(timer);
      var term = input.value;
      timer = window.setTimeout(function () { run(term); }, DEBOUNCE);
    });

    input.addEventListener('focus', function () {
      if (String(input.value || '').trim().length >= MIN_CHARS) {
        if (options.length) { setState('results'); return; }
        run(input.value);
        return;
      }
      showRecent();
    });

    input.addEventListener('keydown', function (event) {
      var key = event.key;

      if (key === 'Escape') {
        if (activeIndex > -1) { setActive(-1); event.preventDefault(); event.stopPropagation(); return; }
        if (!host.hidden) { close(); event.preventDefault(); event.stopPropagation(); }
        return;
      }

      if (host.hidden || !options.length) return;

      if (key === 'ArrowDown' || key === 'Down') { event.preventDefault(); move(1); return; }
      if (key === 'ArrowUp' || key === 'Up') { event.preventDefault(); move(-1); return; }

      /* Home and End stay ordinary text-cursor keys until a suggestion is
         actually highlighted, so editing a query is never hijacked. */
      if (activeIndex > -1 && key === 'Home') { event.preventDefault(); setActive(0); return; }
      if (activeIndex > -1 && key === 'End') { event.preventDefault(); setActive(options.length - 1); return; }

      if (key === 'Enter' && activeIndex > -1) {
        var node = options[activeIndex];
        var href = node && node.getAttribute('href');
        if (href) {
          event.preventDefault();
          rememberTerm(input.value);
          window.location.assign(href);
        }
      }
    });

    /* Pointer and keyboard share one highlight, so two rows never look
       selected at once. mousemove rather than mouseover, so a list scrolling
       under a still cursor does not steal it. */
    results.addEventListener('mousemove', function (event) {
      var node = event.target.closest ? event.target.closest(OPTION_SELECTOR) : null;
      if (!node) return;
      var i = options.indexOf(node);
      if (i > -1 && i !== activeIndex) setActive(i);
    });
    results.addEventListener('mouseleave', function () { setActive(-1); });

    results.addEventListener('click', function (event) {
      var node = event.target.closest ? event.target.closest(OPTION_SELECTOR) : null;
      if (node) rememberTerm(input.value);
    });

    form.addEventListener('submit', function () {
      rememberTerm(input.value);
      close();
    });

    var api = {
      form: form,
      input: input,
      run: run,
      close: close,
      contains: function (node) { return host.contains(node) || form.contains(node); }
    };
    form.dvSuggest = api;
    return api;
  }

  /* ---------------------------------------------------------------- init */
  function init() {
    var made = [];
    $$(FORM_SELECTOR).forEach(function (form) {
      var api = createSuggest(form);
      if (api) made.push(api);
    });
    if (!made.length) return;

    /* Anything outside the field and its list closes it. pointerdown rather
       than blur, so tapping a suggestion on a phone still registers as a tap
       before the field loses focus. */
    document.addEventListener('pointerdown', function (event) {
      if (!openOne) return;
      if (openOne.contains(event.target)) return;
      openOne.close();
    });

    /* A drawer, sheet or dialog opening takes the screen; the list goes. */
    document.addEventListener('dv:overlay:open', function () { if (openOne) openOne.close(); });
    window.addEventListener('pagehide', function () { if (openOne) openOne.close(); });

    /* The search panel snippet has its own microphone. It is bound only when
       that panel is actually rendered; the header's microphone is header.js's
       and is not touched here. */
    bindPanelVoice();
  }

  function bindPanelVoice() {
    var micBtn = document.querySelector('[data-dv-voice]');
    if (!micBtn) return;

    var form = micBtn.closest('form');
    var api = form && form.dvSuggest;
    var input = api && api.input;
    if (!input) return;

    var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { micBtn.remove(); return; }

    micBtn.hidden = false;

    var recognition = null;
    var listening = false;
    var deniedOnce = false;
    var live = document.querySelector('[data-dv-search-status]');

    function say(message) { if (live) live.textContent = message || ''; }

    function stopListening(reason) {
      listening = false;
      micBtn.setAttribute('aria-pressed', 'false');
      micBtn.setAttribute('aria-label', micBtn.getAttribute('data-label-start') || '');
      micBtn.classList.remove('is-listening', 'is-processing');
      if (reason) say(reason);
      if (recognition) { try { recognition.stop(); } catch (e) { /* already stopped */ } }
    }

    function startListening() {
      if (listening) { stopListening(text('cancelled', '')); return; }
      if (deniedOnce) { say(text('denied', '')); return; }

      recognition = new SR();
      recognition.lang = document.documentElement.lang || 'en-IN';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      recognition.continuous = false;

      recognition.onstart = function () {
        listening = true;
        micBtn.setAttribute('aria-pressed', 'true');
        micBtn.setAttribute('aria-label', micBtn.getAttribute('data-label-stop') || '');
        micBtn.classList.add('is-listening');
        say(text('listening', ''));
      };

      recognition.onresult = function (event) {
        micBtn.classList.remove('is-listening');
        micBtn.classList.add('is-processing');
        say(text('processing', ''));

        var transcript = '';
        try { transcript = event.results[0][0].transcript || ''; } catch (e) { transcript = ''; }
        transcript = transcript.trim();
        if (!transcript) { stopListening(text('error', '')); return; }

        /* The shopper stays in control: the text lands in the field, it is
           editable, and nothing is submitted for them. */
        input.value = transcript;
        input.focus();
        try { input.setSelectionRange(transcript.length, transcript.length); } catch (e) { /* not selectable */ }
        stopListening(text('heard', '').replace('%TEXT%', transcript));
        api.run(transcript);
      };

      recognition.onerror = function (event) {
        var code = event && event.error;
        if (code === 'not-allowed' || code === 'service-not-allowed') {
          deniedOnce = true;
          stopListening(text('denied', ''));
          return;
        }
        if (code === 'aborted') { stopListening(text('cancelled', '')); return; }
        stopListening(text('error', ''));
      };

      recognition.onend = function () { if (listening) stopListening(''); };

      try { recognition.start(); } catch (e) { stopListening(text('error', '')); }
    }

    micBtn.addEventListener('click', startListening);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && listening) stopListening(text('cancelled', ''));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
